import { Entity, Metric } from "@/app/types"

import { isNotArray, isNull } from "@/app/utils"

export const VALID_CHART_TYPES = ["periodic", "bar", "scatter", "bubble"]
export const VALID_APPLY_TO = ["fund", "firm", "both"]

export class Chart {
  readonly code: string
  readonly name: string
  readonly slug: string
  readonly chartType: "periodic" | "bar" | "scatter" | "bubble"
  readonly fundOnly: boolean
  readonly firmOnly: boolean
  readonly supportsDaily: boolean
  readonly isTermDurationRelevant: boolean
  top10MetricName: string
  metricNames: string[]
  readonly top10Metric: Readonly<Metric>
  readonly chartMetrics: Readonly<Metric>[]
  readonly tableMetrics: Readonly<Metric>[]

  //back reference to the ChartGroup, populated in the chart_definitions.ts module code
  group: ChartGroup

  // this constructor should only be used in the chart_definitions.ts file/module
  // all other code should use the findChart(), findChartGroup() methods to get the singleton Chart instances
  constructor({
    code,
    name,
    slug,
    chartType,
    applyTo,
    top10,
    chartMetrics,
    tableMetrics = [],
    supportsDaily,
    isTermDurationRelevant,
  }) {
    this.code = code
    this.name = name
    this.slug = slug
    this.chartType = chartType
    this.fundOnly = applyTo && applyTo == "fund"
    this.firmOnly = applyTo && applyTo == "firm"
    this.top10MetricName = top10
    this.metricNames = chartMetrics

    // validate values
    if (!this.code) throw new Error("Chart code cannot be empty")
    if (!this.name) throw new Error("Chart name cannot be empty")
    if (!this.slug) throw new Error("Chart slug cannot be empty")
    if (!VALID_CHART_TYPES.includes(chartType))
      throw new Error(
        `Invalid chartType for chart ${this.name}. Expected:${VALID_CHART_TYPES}, Got:${applyTo}`
      )
    if (!VALID_APPLY_TO.includes(applyTo))
      throw new Error(
        `Invalid applyTo for chart ${this.name}. Expected:${VALID_APPLY_TO}, Got:${applyTo}`
      )

    this.top10Metric = Metric.getMetric(top10)
    if (this.top10Metric === null)
      throw new Error(
        `Chart ${this.name} has invalid top10MetricName. Got:${top10}`
      )
    if (this.top10Metric.fundOnly && this.firmOnly) {
      throw new Error(
        `Chart '${this.name}' is only for firms, but it's top10 metric '${top10.name}' is only for funds.`
      )
    }
    if (this.top10Metric.firmOnly && this.fundOnly) {
      throw new Error(
        `Chart '${this.name}' is only for funds, but it's top10 metric '${top10}' is only for firms.`
      )
    }

    // validate all members of chart metrics, and save the final array in this.chartMetrics
    if (!Array.isArray(chartMetrics))
      throw new Error(
        `Chart ${
          this.name
        } has invalid type for metricnames. Expected:array, Got:${typeof chartMetrics}`
      )
    this.chartMetrics = chartMetrics.map((name, idx) => {
      const metric = Metric.getMetric(name)
      if (metric === null)
        throw new Error(
          `Chart ${this.name} has invalid chart metricName ${name} at idx ${idx}`
        )
      if (metric.fundOnly && this.firmOnly) {
        throw new Error(
          `Chart '${this.name}' is only for firms, but it's metric '${metric.name}' is only for funds.`
        )
      }
      if (metric.firmOnly && this.fundOnly) {
        throw new Error(
          `Chart '${this.name}' is only for funds, but it's metric '${metric.name}' is only for firms.`
        )
      }
      return metric
    })

    // the chart supports daily interval only if all the metrics support it
    this.supportsDaily = this.chartMetrics.every((m) => m.supportsDaily)
    // if any of chart's metrics support this property, return true
    this.isTermDurationRelevant = this.chartMetrics.some(
      (m) => m.isTermDurationRelevant
    )

    // validate all members of table metrics, and save the final array in this.chartMetrics
    if (!Array.isArray(tableMetrics))
      throw new Error(
        `Chart ${
          this.name
        } has invalid type for metricnames. Expected:array, Got:${typeof chartMetrics}`
      )
    this.tableMetrics = tableMetrics.map((name, idx) => {
      const metric = Metric.getMetric(name)
      if (metric === null)
        throw new Error(
          `Chart ${this.name} has invalid table metricName ${name} at idx ${idx}`
        )
      return metric
    })
  }

  isShown(isFund: boolean): boolean {
    return isFund ? !this.firmOnly : !this.fundOnly
  }
  isValidFor(entity: Entity): boolean {
    return entity ? this.isShown(entity.isFund()) : false
  }
  isPeriodic(): boolean {
    return this.chartType == "periodic"
  }
  isBar(): boolean {
    return this.chartType == "bar"
  }
  isScatter(): boolean {
    return this.chartType == "scatter"
  }
  isBubble(): boolean {
    return this.chartType == "bubble"
  }
  groupName(): string {
    return this.group.groupname
  }
  toString(): string {
    return `${this.name}(${this.code})`
  }
}

export class ChartGroup {
  readonly groupname: string
  readonly slug: string
  readonly charts: Chart[]
  readonly chartGroupMetrics: Metric[]

  // this constructor should only be called from chart_definitions.ts
  // other code should use findChartGroup() to use the ChartGroup object from
  // the predefined arrays
  constructor({ groupname, slug, groupMetrics, charts }) {
    this.groupname = groupname
    this.slug = slug
    this.charts = charts
    // map the names to Metric objects
    if (isNotArray(groupMetrics)) {
      throw new Error(
        `ChartGroup ${
          this.groupname
        } has missing groupMetrics. type:${typeof groupMetrics}`
      )
    }
    this.chartGroupMetrics = groupMetrics.map((name) => {
      const metric = Metric.getMetric(name)
      if (isNull(metric))
        throw new Error(
          `ChartGroup ${this.groupname} has invalid chartGroupMetric:${name}`
        )
      return metric
    })
  }
}

export namespace ChartGroup {
  export const GROWTH = "Growth"
  export const NETFLOWS = "Flows"
  export const ASSETS = "Assets"
  export const MKT_SHARE = "Mkt Share"
  export const FEES = "Fees"
  export const PERFORMANCE = "Performance"
}
